/**
 * Layout metrics for responsive shell:
 * - Primary left nav uses a drawer below {@link MOBILE_NAV_MAX_WIDTH_PX} (see Header).
 * - Desktop chat padding matches the original app; narrow viewports use {@link narrowChatSurfaceSx}.
 * - Shell height uses dynamic viewport units so mobile browser chrome does not clip composers.
 */

export const MOBILE_NAV_MAX_WIDTH_PX = 767;

/** Header column width (reference for modals / layout math). */
export const SIDEBAR_COLLAPSED_WIDTH_PX = 56;
export const SIDEBAR_EXPANDED_WIDTH_PX = 240;
export const SIDEBAR_INNER_WIDTH_PX = 200;
export const SIDEBAR_OUTER_PAD_X_PX = 16;

/**
 * Desktop horizontal padding for chat / composer (MUI spacing units) — unchanged from
 * pre-responsive layout so normal screens match the original UI.
 */
export const DESKTOP_CHAT_PADDING_X = 45;

/** Mode strip / history rail — desktop value preserved from original. */
export const DESKTOP_MODE_STRIP_PADDING_X = 4;

/** Desktop shell — original fixed viewport height. */
export const DESKTOP_SHELL_HEIGHT = "100vh";

/**
 * Mobile shell — prefers visualViewport (keyboard-safe), then dvh.
 * Only apply under {@link mobileNavMediaQuery}.
 */
export const MOBILE_SHELL_HEIGHT = "var(--app-vvh, 100dvh)";

/** @deprecated Prefer DESKTOP_SHELL_HEIGHT / MOBILE_SHELL_HEIGHT by breakpoint. */
export const APP_SHELL_HEIGHT = MOBILE_SHELL_HEIGHT;

/** Safe-area helpers for notched devices / iOS Safari. */
export const SAFE_AREA_TOP = "env(safe-area-inset-top, 0px)";
export const SAFE_AREA_BOTTOM = "env(safe-area-inset-bottom, 0px)";

/**
 * Bottom padding so composers / sticky footers clear the browser toolbar + home indicator.
 * Intended for use inside `@media ${mobileNavMediaQuery}` only.
 */
export const composerBottomPadSx = {
  pb: `max(12px, calc(10px + ${SAFE_AREA_BOTTOM}))`,
  boxSizing: "border-box",
};

/** Desktop modal height (unchanged from original). */
export const MODAL_HEIGHT_SM = `calc(100vh - 40px)`;

/** Mobile modal max-height with safe-area clearance. */
export const MODAL_MAX_HEIGHT_XS = `calc(100dvh - 16px - ${SAFE_AREA_BOTTOM})`;

/**
 * Narrow-viewport overrides only (max-width {@link MOBILE_NAV_MAX_WIDTH_PX}).
 * Margins tighten first; content column is capped for readability.
 */
export const narrowChatSurfaceSx = {
  px: 1.5,
  maxWidth: "min(100%, 720px)",
  mx: "auto",
  boxSizing: "border-box",
};

export const desktopNavMediaQuery = `(min-width: ${MOBILE_NAV_MAX_WIDTH_PX + 1}px)`;
export const mobileNavMediaQuery = `(max-width: ${MOBILE_NAV_MAX_WIDTH_PX}px)`;

/**
 * Left inset required to clear the absolute header nav.
 * Used only below lg — wide desktop keeps original chat padding (images 1–2).
 */
export const getDesktopSidebarOffsetPx = (isSidebarOpen) =>
  isSidebarOpen ? SIDEBAR_EXPANDED_WIDTH_PX : SIDEBAR_COLLAPSED_WIDTH_PX;

/** Gap between absolute sidebar and main column on admin/management pages. */
export const DESKTOP_MAIN_GAP_PX = 24;
export const DESKTOP_MAIN_RIGHT_INSET_PX = 24;
export const DESKTOP_MAIN_LEFT_OFFSET_PX =
  SIDEBAR_EXPANDED_WIDTH_PX + DESKTOP_MAIN_GAP_PX;

/**
 * Admin/management main column inset — starts at desktop nav (768px), not MUI md (900px),
 * so content clears the sidebar when DevTools narrows the viewport.
 */
export const desktopMainColumnInsetSx = {
  boxSizing: "border-box",
  ml: 0,
  mr: 0,
  width: "100%",
  minWidth: 0,
  [`@media ${desktopNavMediaQuery}`]: {
    ml: `${DESKTOP_MAIN_LEFT_OFFSET_PX}px`,
    mr: `${DESKTOP_MAIN_RIGHT_INSET_PX}px`,
    width: `calc(100% - ${DESKTOP_MAIN_LEFT_OFFSET_PX + DESKTOP_MAIN_RIGHT_INSET_PX}px)`,
  },
};

/**
 * Mid-desktop only: bump left padding when the default gutters no longer clear
 * the sidebar. Does not change empty-state centering or lg+ layout.
 */
export const midDesktopSidebarClearanceSx = (isSidebarOpen) => {
  const clearPx = getDesktopSidebarOffsetPx(isSidebarOpen) + SIDEBAR_OUTER_PAD_X_PX;
  return {
    // Below md: default chat gutter is xs:4 (32px)
    [`@media (min-width: ${MOBILE_NAV_MAX_WIDTH_PX + 1}px) and (max-width: 899.95px)`]: {
      pl: `${Math.max(32, clearPx)}px`,
    },
    // md–lg: default chat gutter is md:20 (160px)
    "@media (min-width: 900px) and (max-width: 1199.95px)": {
      pl: `${Math.max(160, clearPx)}px`,
    },
  };
};
